if (!customElements.get('product-form')) {
    customElements.define(
        'product-form',
        class ProductForm extends HTMLElement {
            constructor() {
                super();

                this.form = this.querySelector('form');
                this.form.querySelector('[name=id]').disabled = false;
                this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
                this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
                this.submitButton = this.querySelector('[type="submit"]');

                if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

                this.hideErrors = this.dataset.hideErrors === 'true';

                // Load multi-pack cart config
                this.multiPackConfig = this.loadMultiPackConfig();
            }

            /**
             * Load multi-pack cart config from JSON script
             */
            loadMultiPackConfig() {
                const configEl = document.getElementById('multi-pack-cart-config');
                if (!configEl) return null;
                try {
                    return JSON.parse(configEl.textContent);
                } catch (e) {
                    console.error('[MultiPackCart] Failed to parse config:', e);
                    return null;
                }
            }

            /**
             * Check if variant is a multi-pack and get its config
             * @param {string|number} variantId - The variant ID to check
             * @returns {Object|null} - The multi-pack config or null if not a multi-pack
             */
            getMultiPackVariantConfig(variantId) {
                if (!this.multiPackConfig?.variants) return null;
                return this.multiPackConfig.variants[variantId] || null;
            }

            /**
             * Get the selling plan ID for a given pack size
             * @param {number} packCount - The pack count (1, 2, or 4)
             * @returns {number|null} - The selling plan ID or null
             */
            getSellingPlanForPackSize(packCount) {
                if (!this.multiPackConfig?.sellingPlanMap) return null;
                const planId = this.multiPackConfig.sellingPlanMap[packCount];
                return planId && planId !== 'null' && planId !== null ? planId : null;
            }

            /**
             * Add multi-pack items to cart as multiple 1-pack items
             * @param {number} onePackVariantId - The 1-pack variant ID
             * @param {number} quantity - Number of 1-pack units to add
             * @param {number|null} sellingPlanId - Optional selling plan ID for subscription
             * @param {string} bundleType - The bundle label (e.g., "2 Pack")
             */
            async addMultiPackToCart(onePackVariantId, quantity, sellingPlanId, bundleType) {
                const item = {
                    id: onePackVariantId,
                    quantity: quantity,
                    properties: {
                        '_bundle_type': bundleType,
                        'Bundle': bundleType  // Visible in cart
                    }
                };

                if (sellingPlanId) {
                    item.selling_plan = sellingPlanId;
                }

                const body = {
                    items: [item]
                };

                if (this.cart) {
                    body.sections = this.cart.getSectionsToRender().map((section) => section.id);
                    body.sections_url = window.location.pathname;
                    this.cart.setActiveElement(document.activeElement);
                }

                try {
                    console.log('[MultiPackCart] Sending request:', JSON.stringify(body, null, 2));

                    const response = await fetch(`${routes.cart_add_url}`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-Requested-With': 'XMLHttpRequest'
                        },
                        body: JSON.stringify(body)
                    });

                    // Log response status
                    console.log('[MultiPackCart] Response status:', response.status, response.statusText);

                    // Get response as text first to see what we're dealing with
                    const responseText = await response.text();
                    console.log('[MultiPackCart] Response preview:', responseText.substring(0, 500));

                    // Try to parse as JSON
                    let responseData;
                    try {
                        responseData = JSON.parse(responseText);
                    } catch (parseError) {
                        console.warn('[MultiPackCart] Response is not JSON, checking if item was added anyway...');

                        // Check cart to see if item was actually added
                        const cartResponse = await fetch('/cart.js');
                        const cartData = await cartResponse.json();
                        console.log('[MultiPackCart] Current cart items:', cartData.item_count);

                        // If cart has items, assume success and open drawer
                        if (cartData.item_count > 0) {
                            console.log('[MultiPackCart] Items found in cart, opening drawer...');
                            this.error = false;
                            await this.refreshCartDrawerAndOpen();
                            return;
                        }

                        // Otherwise, show error
                        throw new Error('Failed to add to cart: ' + responseText.substring(0, 100));
                    }

                    if (responseData.status) {
                        // Error occurred - log full error details
                        console.error('[MultiPackCart] Cart API error:', responseData);
                        publish(PUB_SUB_EVENTS.cartError, {
                            source: 'product-form',
                            productVariantId: onePackVariantId,
                            errors: responseData.errors || responseData.description,
                            message: responseData.message,
                        });
                        this.handleErrorMessage(responseData.description);
                        this.error = true;
                        return;
                    }

                    // Success
                    console.log('[MultiPackCart] Successfully added to cart');
                    if (!this.cart) {
                        window.location = window.routes.cart_url;
                        return;
                    }

                    this.error = false;
                    publish(PUB_SUB_EVENTS.cartUpdate, {
                        source: 'product-form',
                        productVariantId: onePackVariantId,
                        cartData: responseData,
                    });

                    const quickAddModal = this.closest('quick-add-modal');
                    if (quickAddModal) {
                        document.body.addEventListener(
                            'modalClosed',
                            () => {
                                setTimeout(() => {
                                    this.cart.renderContents(responseData);
                                });
                            },
                            { once: true }
                        );
                        quickAddModal.hide(true);
                    } else {
                        // Check if sections data is present
                        if (responseData.sections) {
                            this.cart.renderContents(responseData);
                        } else {
                            // Fallback: refresh cart drawer manually
                            await this.refreshCartDrawerAndOpen();
                        }
                    }

                } catch (e) {
                    console.error('[MultiPackCart] Add to cart error:', e);
                    // Fallback: try to refresh cart drawer
                    await this.refreshCartDrawerAndOpen();
                } finally {
                    this.submitButton.classList.remove('loading');
                    if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
                    if (!this.error) this.submitButton.removeAttribute('aria-disabled');
                    const spinner = this.querySelector('.loading__spinner');
                    if (spinner) spinner.classList.add('hidden');
                }
            }

            /**
             * Fallback: Refresh cart drawer sections and open it
             */
            async refreshCartDrawerAndOpen() {
                if (!this.cart) return;
                try {
                    const sections = this.cart.getSectionsToRender().map((section) => section.id);
                    const sectionUrl = `${window.location.pathname}?sections=${sections.join(',')}`;
                    const response = await fetch(sectionUrl);
                    const sectionsData = await response.json();
                    this.cart.renderContents({ sections: sectionsData });
                } catch (e) {
                    console.error('[MultiPackCart] Failed to refresh cart drawer:', e);
                    // Last resort: reload page
                    window.location = window.routes.cart_url;
                }
            }

            onSubmitHandler(evt) {
                evt.preventDefault();
                if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

                this.handleErrorMessage();

                this.submitButton.setAttribute('aria-disabled', true);
                this.submitButton.classList.add('loading');
                const spinner = this.querySelector('.loading__spinner');
                if (spinner) spinner.classList.remove('hidden');

                // Get selected variant ID
                const selectedVariantId = this.form.querySelector('[name=id]').value;
                const multiPackVariant = this.getMultiPackVariantConfig(selectedVariantId);

                // Check if this is a multi-pack variant
                if (multiPackVariant) {
                    // Multi-pack - swap to 1-pack items
                    const packCount = multiPackVariant.packCount;
                    const onePackId = multiPackVariant.onePackId || this.multiPackConfig?.onePackVariantId;
                    const bundleType = multiPackVariant.title || `${packCount} Pack`;

                    if (!onePackId) {
                        console.error('[MultiPackCart] No 1-pack variant ID found');
                        this.handleNormalSubmit();
                        return;
                    }

                    // Check for Stay AI subscription
                    const sellingPlanInput = this.form.querySelector('[name="selling_plan"]');
                    const hasSubscription = sellingPlanInput && sellingPlanInput.value;

                    let sellingPlanId = null;
                    if (hasSubscription) {
                        // Get mapped selling plan for this pack size
                        sellingPlanId = this.getSellingPlanForPackSize(packCount);
                        // Fallback to Stay AI's selected plan if no mapping found
                        if (!sellingPlanId) {
                            sellingPlanId = sellingPlanInput.value;
                        }
                    }

                    // Get quantity (default to 1 if not found)
                    const quantityInput = this.form.querySelector('[name="quantity"]');
                    const formQuantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;

                    // Total 1-pack units = pack size * form quantity
                    const totalUnits = packCount * formQuantity;

                    console.log('[MultiPackCart] Adding multi-pack:', {
                        packCount,
                        formQuantity,
                        totalUnits,
                        onePackId,
                        sellingPlanId,
                        bundleType
                    });

                    this.addMultiPackToCart(onePackId, totalUnits, sellingPlanId, bundleType);
                    return;
                }

                // Normal flow for 1-pack or non-multi-pack products
                this.handleNormalSubmit();
            }

            /**
             * Handle normal (non-multi-pack) form submission
             */
            handleNormalSubmit() {
                const config = fetchConfig('javascript');
                config.headers['X-Requested-With'] = 'XMLHttpRequest';
                delete config.headers['Content-Type'];

                const formData = new FormData(this.form);
                if (this.cart) {
                    formData.append(
                        'sections',
                        this.cart.getSectionsToRender().map((section) => section.id)
                    );
                    formData.append('sections_url', window.location.pathname);
                    this.cart.setActiveElement(document.activeElement);
                }
                config.body = formData;

                fetch(`${routes.cart_add_url}`, config)
                    .then((response) => response.json())
                    .then((response) => {
                        if (response.status) {
                            publish(PUB_SUB_EVENTS.cartError, {
                                source: 'product-form',
                                productVariantId: formData.get('id'),
                                errors: response.errors || response.description,
                                message: response.message,
                            });
                            this.handleErrorMessage(response.description);

                            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
                            if (!soldOutMessage) return;
                            this.submitButton.setAttribute('aria-disabled', true);
                            this.submitButton.querySelector('span').classList.add('hidden');
                            soldOutMessage.classList.remove('hidden');
                            this.error = true;
                            return;
                        } else if (!this.cart) {
                            window.location = window.routes.cart_url;
                            return;
                        }

                        if (!this.error)
                            publish(PUB_SUB_EVENTS.cartUpdate, {
                                source: 'product-form',
                                productVariantId: formData.get('id'),
                                cartData: response,
                            });
                        this.error = false;
                        const quickAddModal = this.closest('quick-add-modal');
                        if (quickAddModal) {
                            document.body.addEventListener(
                                'modalClosed',
                                () => {
                                    setTimeout(() => {
                                        this.cart.renderContents(response);
                                    });
                                },
                                { once: true }
                            );
                            quickAddModal.hide(true);
                        } else {
                            this.cart.renderContents(response);
                        }
                    })
                    .catch((e) => {
                        console.error(e);
                    })
                    .finally(() => {
                        this.submitButton.classList.remove('loading');
                        if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
                        if (!this.error) this.submitButton.removeAttribute('aria-disabled');
                        const spinner = this.querySelector('.loading__spinner');
                        if (spinner) spinner.classList.add('hidden');
                    });
            }

            handleErrorMessage(errorMessage = false) {
                if (this.hideErrors) return;

                this.errorMessageWrapper =
                    this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
                if (!this.errorMessageWrapper) return;
                this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

                this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

                if (errorMessage) {
                    this.errorMessage.textContent = errorMessage;
                  setTimeout(() => {
                        if(jQuery('.product-form__error-message').is(':visible')){
                            location.reload();
                        }
                    }, "1500");
                }
            }
        }
    );
}
